import { GraphConfiguration } from "@/type/common";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { DynamicLucideIcon } from "../puck/PageIconSelector";
import { cleanDomain, refactor_config } from "@/utils/refactor";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface BlockData {
  value: number;
  label: string;
}

const DataBlockItem = ({
  config,
  data,
}: {
  config: GraphConfiguration;
  data: any;
}) => {
  const { t } = useTranslation();
  // Check if we have block data (object with value and label)
  const isBlockData = data && typeof data === "object" && "value" !== undefined;
  const blockData = isBlockData ? (data as BlockData) : null;

  // Format the value according to block_options if available
  const formatValue = (value: number) => {
    if (!config.block_options) return value;

    const { format, decimals, prefix, suffix } = config.block_options;
    let formattedValue: string | number = value;

    // Format based on the specified format
    try {
      switch (format) {
        case "number":
          formattedValue = Number(
            value.toFixed(decimals || 0)
          ).toLocaleString();
          break;
        case "decimal":
          formattedValue = Number(
            value.toFixed(decimals || 2)
          ).toLocaleString();
          break;
        case "percent":
          formattedValue = Number(
            (value * 100).toFixed(decimals || 0)
          ).toLocaleString();
          break;
        case "currency":
          formattedValue = Number(
            value.toFixed(decimals || 2)
          ).toLocaleString();
          break;
        case "compact":
          formattedValue =
            value >= 1000000
              ? Number(
                  (value / 1000000).toFixed(decimals || 1)
                ).toLocaleString()
              : value >= 1000
              ? Number((value / 1000).toFixed(decimals || 1)).toLocaleString()
              : Number(value.toFixed(decimals || 0)).toLocaleString();
          break;
      }
    } catch (error) {
      formattedValue = value;
    }

    return `${prefix || ""}${formattedValue}${format === "percent" ? "%" : ""}${
      suffix || ""
    }`;
  };

  if (
    blockData &&
    config.data_source?.sqlRequest &&
    config.block_options?.field
  ) {
    blockData.value = blockData[
      config.block_options.field as keyof BlockData
    ] as any;
  }

  return (
    <Card
      className="gap-2 h-full w-full p-4 cursor-pointer"
      onClick={() => {
        if (config.data_source.sqlRequest) {
          toast.info("You can't navigate on a graph with a SQL request");
          return;
        }
        if (window.location.href.includes("/edit")) {
          toast.info("You can't navigate on a graph in edit mode");
          return;
        }
        const correctedConfig = refactor_config(config);
        const target = {
          model: correctedConfig.model,
          type: "navigate",
          name: correctedConfig.title,
          domain: cleanDomain(correctedConfig.data_source.domain),
        };
        window.parent.postMessage(target, "*");
      }}
    >
      <CardHeader className="px-0">
        <div className="flex items-center justify-between">
          <CardTitle>
            {config.title && config.title !== ""
              ? config.title
              : t("editor.graphConfig.data.unNamedBlock")}
          </CardTitle>
          {config.block_options?.icon &&
            config.block_options.icon !== "none" && (
              <DynamicLucideIcon name={config.block_options.icon} size={18} />
            )}
        </div>
      </CardHeader>
      <CardContent className="flex items-center justify-center h-full">
        {blockData && blockData.value != undefined ? (
          <div className="text-center">
            <div className="text-3xl font-bold">
              {formatValue(blockData.value)}
            </div>
          </div>
        ) : (
          <Skeleton className="w-full h-12" />
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(DataBlockItem);
